import React, { useState, useEffect, useRef, RefObject } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Gift, Send, Loader2, MapPin, User, Phone, Home } from 'lucide-react';
import { RewardShipment } from '@/types/missions';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { rewardShipmentSchema, RewardShipmentInput } from '@/lib/validation/schemas';
import { fetchAddressByCep, formatCep, formatPhone } from '@/utils/cepUtils';
import { vibrateError, vibrateSuccess } from '@/utils/hapticFeedback';
import './RewardForm.css';

interface RewardFormProps {
  onSubmit: (data: Omit<RewardShipment, 'id' | 'user_id' | 'mission_id' | 'sent' | 'submitted_at' | 'created_at'>) => Promise<boolean>;
}

const RewardForm: React.FC<RewardFormProps> = ({ onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [fetchingCep, setFetchingCep] = useState(false);
  const lastFetchedCep = useRef<string>('');

  // Refs para cada campo
  const nameRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const zipRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const complementRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<RewardShipmentInput>({
    resolver: zodResolver(rewardShipmentSchema),
    defaultValues: {
      full_name: '',
      phone: '',
      zip_code: '',
      address: '',
      number: '',
      complement: '',
    },
  });

  // Mapeamento de campos para refs
  const fieldRefs: Record<keyof RewardShipmentInput, RefObject<HTMLDivElement>> = {
    full_name: nameRef,
    phone: phoneRef,
    zip_code: zipRef,
    address: addressRef,
    number: numberRef,
    complement: complementRef,
  };

  // Hook para detectar erros e fazer scroll
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // Vibrar para indicar erro
      vibrateError();
      
      // Encontrar primeiro campo com erro
      const firstErrorField = Object.keys(errors)[0] as keyof RewardShipmentInput;
      const fieldRef = fieldRefs[firstErrorField];
      
      if (fieldRef?.current) {
        // Scroll suave até o campo
        fieldRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Adicionar classe de erro para animação
        const input = fieldRef.current.querySelector('input');
        if (input) {
          input.classList.add('field-error');
          // Remover após a animação
          setTimeout(() => {
            input.classList.remove('field-error');
          }, 500);
        }
        
        // Focar no campo
        const inputElement = fieldRef.current.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }
    }
  }, [errors]);

  const zipCode = watch('zip_code');

  useEffect(() => {
    const searchCep = async () => {
      const cleanCep = zipCode?.replace(/\D/g, '');
      
      // Só buscar se:
      // 1. CEP tem 8 dígitos
      // 2. É diferente do último CEP buscado
      // 3. Não está buscando no momento
      if (
        cleanCep?.length === 8 && 
        cleanCep !== lastFetchedCep.current && 
        !fetchingCep
      ) {
        lastFetchedCep.current = cleanCep; // Marcar como buscado ANTES da chamada
        setFetchingCep(true);
        
        const data = await fetchAddressByCep(cleanCep);
        
        if (data && !data.erro) {
          const fullAddress = [
            data.logradouro,
            data.bairro,
            data.localidade,
            data.uf
          ].filter(Boolean).join(', ');
          
          setValue('address', fullAddress);
        }
        
        setFetchingCep(false);
      }
    };
    
    const timeoutId = setTimeout(searchCep, 500); // Debounce de 500ms
    return () => clearTimeout(timeoutId);
  }, [zipCode, setValue]);

  const onFormSubmit = async (data: RewardShipmentInput) => {
    setSubmitting(true);
    
    const success = await onSubmit(data);
    
    setSubmitting(false);
    
    if (success) {
      vibrateSuccess(); // Vibração de sucesso
      reset();
      lastFetchedCep.current = ''; // Reset CEP reference
    }
  };

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-purple-900/30 border border-purple-500/30">
            <Gift className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Informações de Envio
            </h3>
            <p className="text-sm text-muted-foreground">
              Preencha para receber seu presente
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {/* Full Name */}
          <div ref={nameRef}>
            <Label htmlFor="full_name" className={`text-white flex items-center gap-2 mb-2 ${errors.full_name ? 'field-error-label' : ''}`}>
              <User className="w-4 h-4" />
              Nome Completo
            </Label>
            <Input
              id="full_name"
              {...register('full_name')}
              placeholder="Seu nome completo"
              className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.full_name ? 'border-red-500' : ''}`}
            />
            {errors.full_name && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.full_name.message}
              </p>
            )}
          </div>

          {/* Phone */}
          <div ref={phoneRef}>
            <Label htmlFor="phone" className={`text-white flex items-center gap-2 mb-2 ${errors.phone ? 'field-error-label' : ''}`}>
              <Phone className="w-4 h-4" />
              Telefone/WhatsApp
            </Label>
            <Input
              id="phone"
              {...register('phone', {
                onChange: (e) => {
                  const formatted = formatPhone(e.target.value);
                  setValue('phone', formatted);
                }
              })}
              placeholder="(11) 99999-9999"
              maxLength={15}
              className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.phone ? 'border-red-500' : ''}`}
            />
            {errors.phone && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* CEP */}
          <div ref={zipRef}>
            <Label htmlFor="zip_code" className={`text-white flex items-center gap-2 mb-2 ${errors.zip_code ? 'field-error-label' : ''}`}>
              <MapPin className="w-4 h-4" />
              CEP
            </Label>
            <div className="relative">
              <Input
                id="zip_code"
                {...register('zip_code', {
                  onChange: (e) => {
                    const formatted = formatCep(e.target.value);
                    setValue('zip_code', formatted);
                  }
                })}
                placeholder="00000-000"
                maxLength={9}
                className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.zip_code ? 'border-red-500' : ''}`}
              />
              {fetchingCep && (
                <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-purple-400" />
              )}
            </div>
            {errors.zip_code && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.zip_code.message}
              </p>
            )}
          </div>

          {/* Address */}
          <div ref={addressRef}>
            <Label htmlFor="address" className={`text-white flex items-center gap-2 mb-2 ${errors.address ? 'field-error-label' : ''}`}>
              <Home className="w-4 h-4" />
              Logradouro
            </Label>
            <Input
              id="address"
              {...register('address')}
              placeholder="Rua, bairro, cidade - UF"
              className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.address ? 'border-red-500' : ''}`}
            />
            {errors.address && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.address.message}
              </p>
            )}
          </div>

          {/* Number */}
          <div ref={numberRef}>
            <Label htmlFor="number" className={`text-white flex items-center gap-2 mb-2 ${errors.number ? 'field-error-label' : ''}`}>
              <Home className="w-4 h-4" />
              Número da Residência
            </Label>
            <Input
              id="number"
              {...register('number')}
              placeholder="Ex: 123, S/N"
              maxLength={10}
              className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.number ? 'border-red-500' : ''}`}
            />
            {errors.number && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.number.message}
              </p>
            )}
          </div>

          {/* Complement */}
          <div ref={complementRef}>
            <Label htmlFor="complement" className={`text-white mb-2 block ${errors.complement ? 'field-error-label' : ''}`}>
              Complemento (opcional)
            </Label>
            <Input
              id="complement"
              {...register('complement')}
              placeholder="Apto, bloco, etc."
              className={`bg-black/30 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-500/50 focus:ring-purple-500/20 ${errors.complement ? 'border-red-500' : ''}`}
            />
            {errors.complement && (
              <p className="text-red-400 text-sm mt-1 font-medium flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-red-400"></span>
                {errors.complement.message}
              </p>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={submitting}
            className="w-full bg-luxury-red hover:bg-luxury-red/90 border-luxury-red/50 hover:border-luxury-red/70"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar Informações
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default RewardForm;
